/**
 * Auth state management — Google OAuth + JWT tokens
 */

import { AUTH_API, GOOGLE_CLIENT_ID } from './config'

interface AuthUser {
  id: string
  email: string
  first_name: string
  last_name: string
  is_superadmin: boolean
  roles: Array<{ sys: string; role: string; tid: string | null }>
}

interface AuthState {
  user: AuthUser | null
  accessToken: string | null
  refreshToken: string | null
}

const STORAGE_KEY = 'content_auth'

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

let state: AuthState = load()

function load(): AuthState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) return JSON.parse(raw)
  } catch { /* ignore */ }
  return { user: null, accessToken: null, refreshToken: null }
}

function persist() {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
}

// Listeners for React re-renders
type Listener = () => void
const listeners = new Set<Listener>()

export function subscribe(fn: Listener) {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

function notify() {
  listeners.forEach(fn => fn())
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function getUser(): AuthUser | null {
  return state.user
}

export function getAccessToken(): string | null {
  return state.accessToken
}

export function isLoggedIn(): boolean {
  return !!state.accessToken && !!state.user
}

export function logout() {
  if (state.refreshToken) {
    fetch(`${AUTH_API}/api/v1/auth/logout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: state.refreshToken }),
    }).catch(() => {})
  }
  state = { user: null, accessToken: null, refreshToken: null }
  localStorage.removeItem(STORAGE_KEY)
  notify()
}

// ---------------------------------------------------------------------------
// Google OAuth flow
// ---------------------------------------------------------------------------

/**
 * E-mail/password login against auth-api (POST /api/v1/auth/login). The
 * default path on tenant instances without a Google OAuth client; lands
 * the same JWT pair + user shape as the Google callback.
 */
export async function loginEmail(email: string, password: string): Promise<void> {
  const resp = await fetch(`${AUTH_API}/api/v1/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  })
  if (!resp.ok) {
    let detail = ''
    try { detail = (await resp.json())?.detail || '' } catch { /* text below */ }
    if (!detail) detail = await resp.text().catch(() => '')
    if (resp.status === 401 || resp.status === 403) {
      throw new Error('E-Mail oder Passwort ist falsch.')
    }
    throw new Error(typeof detail === 'string' && detail ? detail : `Login fehlgeschlagen (HTTP ${resp.status})`)
  }
  const data = await resp.json()
  state = {
    user: data.user,
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
  }
  persist()
  notify()
}

export function startGoogleLogin() {
  if (!GOOGLE_CLIENT_ID) {
    throw new Error('Google-Login ist auf dieser Instanz nicht konfiguriert (VITE_GOOGLE_CLIENT_ID).')
  }
  const redirectUri = `${window.location.origin}/auth/google/callback`
  const params = new URLSearchParams({
    client_id: GOOGLE_CLIENT_ID,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope: 'openid email profile',
    access_type: 'offline',
    prompt: 'select_account',
  })
  window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params}`
}

export async function handleGoogleCallback(code: string): Promise<void> {
  const redirectUri = `${window.location.origin}/auth/google/callback`

  const resp = await fetch(`${AUTH_API}/api/v1/auth/google/callback`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, redirect_uri: redirectUri }),
  })

  if (!resp.ok) {
    const err = await resp.text()
    throw new Error(err)
  }

  const data = await resp.json()
  state = {
    user: data.user,
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
  }
  persist()
  notify()
}

// ---------------------------------------------------------------------------
// Phase 2 (Self-Signup) — new public endpoints (default-gated by
// auth-api's ALLOW_SELF_SIGNUP env flag; 503 until flipped). Each call
// lands a JWT-pair + user shape just like the existing Google-callback
// path so React can re-render straight from `state`.
// ---------------------------------------------------------------------------

export interface SignupEmailRequest {
  email: string
  password: string
  first_name?: string
  last_name?: string
  next_url?: string
}

export interface SignupResponse {
  user: AuthUser
  access_token: string
  refresh_token: string
  tenant_id?: string
  verification?: {
    mail_sent: boolean
    verify_url?: string
  }
}

export class SignupGatedError extends Error {
  // 503 = ALLOW_SELF_SIGNUP=false on the auth-api .env, instance is not
  // open to public registration yet.
  constructor() {
    super('Signup is not enabled yet — please contact the administrator.')
    this.name = 'SignupGatedError'
  }
}

export async function signupEmail(req: SignupEmailRequest): Promise<SignupResponse> {
  const resp = await fetch(`${AUTH_API}/api/v1/auth/signup/email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  })
  if (resp.status === 503) throw new SignupGatedError()
  if (!resp.ok) {
    const txt = await resp.text()
    throw new Error(`Signup failed (${resp.status}): ${txt.slice(0, 200)}`)
  }
  const data = (await resp.json()) as SignupResponse
  state = {
    user: data.user,
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
  }
  persist()
  notify()
  return data
}

export function startGoogleSignup(nextUrl?: string) {
  if (!GOOGLE_CLIENT_ID) {
    throw new Error('Google-Signup ist auf dieser Instanz nicht konfiguriert (VITE_GOOGLE_CLIENT_ID).')
  }
  // Mirrors startGoogleLogin but routes through /signup/google so the
  // self-signup-bypass kicks in for unknown emails.
  const redirectUri = `${window.location.origin}/auth/google/callback`
  // State carries the mode + next-url so the callback knows to call
  // /auth/signup/google (vs /auth/google/callback for plain login).
  const state_payload = JSON.stringify({ mode: 'signup', next: nextUrl || '/' })
  const params = new URLSearchParams({
    client_id: GOOGLE_CLIENT_ID,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope: 'openid email profile',
    access_type: 'offline',
    prompt: 'select_account',
    state: btoa(state_payload),
  })
  window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params}`
}

export async function handleGoogleSignupCallback(code: string): Promise<SignupResponse> {
  const redirectUri = `${window.location.origin}/auth/google/callback`
  const resp = await fetch(`${AUTH_API}/api/v1/auth/signup/google`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, redirect_uri: redirectUri }),
  })
  if (resp.status === 503) throw new SignupGatedError()
  if (!resp.ok) {
    const txt = await resp.text()
    throw new Error(`Google signup failed (${resp.status}): ${txt.slice(0, 200)}`)
  }
  const data = (await resp.json()) as SignupResponse
  state = {
    user: data.user,
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
  }
  persist()
  notify()
  return data
}

export interface VerifyEmailResponse {
  verified: boolean
  already_verified: boolean
  email: string
  next_url: string | null
}

export async function verifyEmail(token: string, next?: string): Promise<VerifyEmailResponse> {
  const qs = new URLSearchParams({ token })
  if (next) qs.set('next', next)
  const resp = await fetch(`${AUTH_API}/api/v1/auth/verify-email?${qs}`)
  if (!resp.ok) {
    const txt = await resp.text()
    throw new Error(`Verify failed (${resp.status}): ${txt.slice(0, 200)}`)
  }
  return (await resp.json()) as VerifyEmailResponse
}

// ---------------------------------------------------------------------------
// Token refresh
// ---------------------------------------------------------------------------

export async function refreshAccessToken(): Promise<boolean> {
  if (!state.refreshToken) return false

  try {
    const resp = await fetch(`${AUTH_API}/api/v1/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: state.refreshToken }),
    })

    if (!resp.ok) {
      logout()
      return false
    }

    const data = await resp.json()
    state.accessToken = data.access_token
    state.refreshToken = data.refresh_token
    persist()
    notify()
    return true
  } catch {
    logout()
    return false
  }
}
